import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { buttonClass, type ButtonSize, type ButtonVariant } from "./buttonStyles";

export type { ButtonSize, ButtonVariant } from "./buttonStyles";

/** Arrow that slides forward on hover, drawn in the button's current colour. */
export function ButtonArrow() {
  return (
    <ArrowRight
      aria-hidden="true"
      className="size-4 shrink-0 transition-transform duration-300 ease-[var(--ease-out-expo)] group-hover/btn:translate-x-1"
    />
  );
}

type CommonProps = {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  arrow?: boolean;
  icon?: ReactNode;
};

export function Button({
  children,
  variant,
  size,
  className,
  arrow,
  icon,
  ...rest
}: CommonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">) {
  return (
    <button type="button" className={buttonClass(variant, size, className)} {...rest}>
      {icon}
      <span>{children}</span>
      {arrow ? <ButtonArrow /> : null}
    </button>
  );
}

export function ButtonLink({
  children,
  variant,
  size,
  className,
  arrow,
  icon,
  to,
  ...rest
}: CommonProps & { to: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "href">) {
  const cls = buttonClass(variant, size, className);
  const inner = (
    <>
      {icon}
      <span>{children}</span>
      {arrow ? <ButtonArrow /> : null}
    </>
  );
  if (/^(https?:|mailto:|tel:)/.test(to)) {
    const external = to.startsWith("http");
    return (
      <a
        href={to}
        className={cls}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        {...rest}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} data-cta="" {...rest}>
      {inner}
    </Link>
  );
}

/** Quiet inline link with a sliding arrow, for secondary actions. */
export function TextLink({
  to,
  children,
  className = "",
  tone = "ink",
}: {
  to: string;
  children: ReactNode;
  className?: string;
  tone?: "ink" | "light";
}) {
  const color =
    tone === "light"
      ? "text-white decoration-white/40 hover:decoration-white"
      : "text-brand-primary decoration-brand-secondary/70 hover:decoration-brand-primary";
  return (
    <Link
      to={to}
      data-cta=""
      className={`group/btn inline-flex items-center gap-2 font-semibold underline decoration-2 underline-offset-[6px] transition-colors ${color} ${className}`}
    >
      {children}
      <ButtonArrow />
    </Link>
  );
}
